import React, { useCallback, useEffect, useState } from "react";
import { CalendarDays, Map as MapIcon, Plus } from "lucide-react";
import { endOfMonth, endOfWeek, startOfMonth, startOfWeek } from "date-fns";
import { toast } from "sonner";
import AppLayout from "../components/AppLayout";
import PlanningCalendar from "../components/planning/PlanningCalendar";
import PlanningMapView from "../components/planning/PlanningMapView";
import RdvDialog from "../components/planning/RdvDialog";
import { api } from "../lib/api";
import { errMsg, RDV_CHANGED, toLocal, usePlanningOptions } from "../lib/planning";

const rangeOf = (view, d) => (view === "month" ? [startOfWeek(startOfMonth(d), { weekStartsOn: 1 }), endOfWeek(endOfMonth(d), { weekStartsOn: 1 })]
  : view === "day" ? [new Date(d.getFullYear(), d.getMonth(), d.getDate()), new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)]
  : view === "agenda" ? [d, new Date(d.getTime() + 31 * 86400000)] : [startOfWeek(d, { weekStartsOn: 1 }), endOfWeek(d, { weekStartsOn: 1 })]);

function Filters({ opts, f, set }) {
  const sel = "input-field !w-auto !py-1.5 text-sm";
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="planning-filters">
      <select value={f.who} onChange={(e) => set({ who: e.target.value })} className={sel} data-testid="planning-filter-who">
        <option value="">Tous les intervenants</option>
        <optgroup label="Techniciens">{opts.techniciens.map((t) => <option key={t.id} value={`t:${t.id}`}>{t.nom}</option>)}</optgroup>
        <optgroup label="Équipes">{opts.equipes.map((t) => <option key={t.id} value={`e:${t.id}`}>{t.nom}</option>)}</optgroup>
      </select>
      <select value={f.type} onChange={(e) => set({ type: e.target.value })} className={sel} data-testid="planning-filter-type">
        <option value="">Tous les types</option>{opts.types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}</select>
      <select value={f.status} onChange={(e) => set({ status: e.target.value })} className={sel} data-testid="planning-filter-status">
        <option value="">Tous les statuts</option>{opts.statuses.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}</select>
    </div>
  );
}

export default function PlanningPage() {
  const [opts] = usePlanningOptions();
  const [mode, setMode] = useState("calendar");
  const [view, setView] = useState("week");
  const [date, setDate] = useState(new Date());
  const [f, setF] = useState({ who: "", type: "", status: "" });
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(null);
  const load = useCallback(() => {
    const [a, b] = rangeOf(view, date);
    const params = { start: toLocal(a), end: toLocal(b), type: f.type || undefined, status: f.status || undefined,
      technicien_id: f.who.startsWith("t:") ? f.who.slice(2) : undefined, equipe_id: f.who.startsWith("e:") ? f.who.slice(2) : undefined };
    api.get("/planning/rdv", { params }).then((r) => setItems(r.data.items)).catch(() => toast.error("Chargement du planning impossible"));
  }, [view, date, f]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.addEventListener(RDV_CHANGED, load); return () => window.removeEventListener(RDV_CHANGED, load); }, [load]);
  const move = async ({ event, start, end }) => {
    try { await api.post(`/planning/rdv/${event.id}/move`, { start: toLocal(start), end: toLocal(end) }); toast.success("Rendez-vous déplacé"); load(); }
    catch (e) { toast.error(errMsg(e, "Déplacement impossible")); load(); }
  };
  const slot = ({ start, end }) => setDialog({ preset: view === "month" ? { start: `${toLocal(start).slice(0, 10)}T09:00` } : { start: toLocal(start), end: toLocal(end) } });
  const tab = (id, Icon, label) => (
    <button onClick={() => setMode(id)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded transition-colors ${mode === id ? "bg-norea-primary text-white" : "text-norea-textMuted hover:text-norea-text"}`} data-testid={`planning-mode-${id}`}><Icon className="w-4 h-4" />{label}</button>
  );
  return (
    <AppLayout>
      <div className="px-8 py-6 space-y-4" data-testid="planning-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div><div className="overline">Interventions & rendez-vous</div><h1 className="font-heading text-3xl font-medium text-norea-text">Planning</h1></div>
          <div className="flex items-center gap-2">
            <div className="inline-flex p-0.5 rounded-md border border-norea-border bg-white">{tab("calendar", CalendarDays, "Calendrier")}{tab("map", MapIcon, "Carte & tournées")}</div>
            <button onClick={() => setDialog({})} className="btn-primary" data-testid="planning-new-rdv"><Plus className="w-4 h-4" />Nouveau rendez-vous</button>
          </div>
        </div>
        {opts && <Filters opts={opts} f={f} set={(p) => setF((x) => ({ ...x, ...p }))} />}
        {mode === "calendar" && <PlanningCalendar items={items} view={view} date={date} onView={setView} onNavigate={setDate} onRange={() => {}}
          onSelect={(r) => setDialog({ rdv: r })} onSlot={slot} onMove={move} />}
        {mode === "map" && opts && <PlanningMapView opts={opts} filters={f} onOpenRdv={(id) => api.get(`/planning/rdv/${id}`).then((r) => setDialog({ rdv: r.data }))} />}
      </div>
      {dialog && <RdvDialog rdv={dialog.rdv} preset={dialog.preset} onClose={() => setDialog(null)} onSaved={load} />}
    </AppLayout>
  );
}
